import { QuoteVariantSelector } from './QuoteVariantSelector'
import { RFQ_QUOTE_VARIANTS } from './domain'
import type { QuoteVariant } from './domain'
import {
  allLinesPriced,
  autoPricedCustomCandidates,
  previewQuoteOptionTotals,
} from './review-domain'
import type { RfqReviewLine } from './review-domain'

export interface RfqPreQuotePricingPreviewProps {
  readonly lines: readonly RfqReviewLine[]
  readonly selectedVariantId: QuoteVariant['id'] | undefined
  readonly onSelect: (id: QuoteVariant['id']) => void
}

/**
 * UID-260 gap #2 (frontend-only follow-up), RE-GATED by UID-401 TASK C — a
 * client-side, PREVIEW-ONLY "Choose a quote option" selector rendered on the
 * RFQ DRAFT (pre-quote) screen. Reuses the SAME presentational
 * `QuoteVariantSelector` (`components.md`'s purple radio-dot variant
 * chooser) and the SAME canonical `RFQ_QUOTE_VARIANTS` ratio table the real
 * post-quote flow reads (ADR-0051) — see `domain.ts`'s doc comment on why
 * this frontend mirror exists.
 *
 * UID-401 TASK C (overturns the pre-401 gate) — BEFORE: this section
 * rendered whenever `autoPricedCustomCandidates(lines).length > 0`, i.e. as
 * soon as ONE candidate line existed, regardless of whether every other
 * line (or that candidate itself) was actually priced. The design's own
 * gate is STRICTER and in the same direction as the ticket's polish item
 * ("shouldn't render until at least one option is checkout-able"):
 * `quoteView` is non-null only when `allPriced` — `rowsData.length>0 &&
 * unpricedCount===0` (`dc.html:~9680`/`:9687`). AFTER: gated on
 * `allLinesPriced(lines)` (TASK A) — every line, not just one candidate,
 * must be priced.
 *
 * Because the gate is now "every line is priced," the design's own literal
 * framing copy is no longer misleading and is used verbatim (the earlier
 * "Preview a quote option" / "Estimated pricing…" override existed
 * specifically because SOME lines might not be priced yet — that caveat no
 * longer applies once this component only renders at `allLinesPriced`).
 * `QuoteVariantSelector`'s defaults ARE the design's literal chip
 * ("All parts priced"), heading ("Choose a quote option"), and subcopy
 * ("Pick a sourcing region and check out from the summary on the right.",
 * `dc.html:4115-4129`) — this component simply stops overriding them.
 *
 * ***THIS IS STILL NOT A REAL QUOTE.*** No `Quote` (ADR-0051) exists yet at
 * this pre-quote draft stage — there is no linked `RfqView.variants` seam to
 * read. Every number this renders is a CLIENT-SIDE ESTIMATE computed from
 * each candidate line's `indicativePrice × ratio` (`previewQuoteOptionTotals`,
 * money-safe via `applyVariantRatio`/`multiplyAmount`/`addAmounts` — never a
 * float multiply), refreshed live as parts/quantities change. It is
 * DELIBERATELY never persisted anywhere and never posted to the wire —
 * selecting an option here only drives this screen's own price-range cells
 * (`PartsReviewTable`'s per-row preview) to collapse from a range into a
 * single number; it does not change what gets submitted with the RFQ. The
 * real, authoritative `QuoteVariantSelector` usage
 * (`RfqCard.tsx`/`QuotedLinesPanel`) is completely untouched by this
 * component and remains the only place a REAL quote-option selection is
 * ever made, once the server has actually returned a priced Quote.
 *
 * The mandatory preview-only caveat is KEPT (money-path reasons, ADR-0051)
 * even though the design has no such caveat — removing it would be a
 * money-path regression, not a fidelity win (UID-401 explicit instruction).
 * Rendered as PLAIN, always-visible text (not hover-only tooltip copy) so it
 * is never missed by a screen reader or a user who never hovers —
 * accessibility-wcag skill guidance against relying on hover-only
 * affordances for essential information.
 */
export function RfqPreQuotePricingPreview({
  lines,
  selectedVariantId,
  onSelect,
}: RfqPreQuotePricingPreviewProps) {
  const candidates = autoPricedCustomCandidates(lines)
  // UID-401 Gate-5 (regression caught in review) — BOTH conditions are
  // required, and neither implies the other.
  //
  // `allLinesPriced` alone is NOT sufficient. TASK C replaced the pre-401
  // guard (`candidates.length === 0`) with the design's stricter
  // `allPriced` gate, but "every line is priced" does not imply "some line
  // is a regional-preview candidate": `isAutoPricedCustomCandidate`
  // EXCLUDES catalog lines (flat, region-independent price — see its doc
  // comment), so an ALL-CATALOG draft is fully priced with ZERO candidates.
  // `previewQuoteOptionTotals` then sums an empty candidate set to
  // `{us:'0.00', offshore:'0.00', prefer_us_blend:'0.00'}` and this section
  // rendered "Choose a quote option" offering three sourcing regions at
  // **$0.00 /ea** — a placeholder money row of exactly the kind ADR-0100 D1
  // forbids by name ("not as `$0.00`, not as `—`, not as a placeholder
  // row"), inviting the customer to pick a region for a zero-dollar quote.
  //
  // `candidates.length > 0` alone is NOT sufficient either — that is the
  // pre-401 gate this ticket deliberately tightened (TASK C), which let the
  // section render while other lines were still unpriced.
  //
  // Both guards are pinned by their own regression tests; do not collapse
  // this back to one condition.
  if (candidates.length === 0 || !allLinesPriced(lines)) return null

  const currency = candidates[0]?.sourceable.indicativePrice?.currency ?? 'USD'
  const previewAmounts = previewQuoteOptionTotals(lines, RFQ_QUOTE_VARIANTS)
  // Defaulting the radio group's checked option to 'us' is a pure display
  // convenience (`QuoteVariantSelector.selectedVariantId` isn't optional) —
  // the per-row range-vs-single-price cells in `PartsReviewTable` key off
  // `selectedVariantId` from `RfqReviewPage` directly (`undefined` until the
  // user actually picks one), NOT off this default, so a row still shows the
  // full range until a real selection happens here.
  const displayedSelectedId: QuoteVariant['id'] =
    selectedVariantId ?? RFQ_QUOTE_VARIANTS[0]?.id ?? 'us'

  return (
    <div className="rfq-preview-quote-option">
      <p className="rfq-preview-quote-option-caveat">
        Preview only — not a real quote. These prices are a client-side estimate before your RFQ is
        submitted and quoted; actual pricing, availability, and lead times are confirmed once we
        return your real quote.
      </p>
      <QuoteVariantSelector
        variants={RFQ_QUOTE_VARIANTS}
        selectedVariantId={displayedSelectedId}
        onSelect={onSelect}
        previewAmounts={previewAmounts}
        currency={currency}
      />
    </div>
  )
}
